import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePosts } from '../posts/hooks/usePosts';
import { Loading } from './ui/Loading';
import BannerDnd from '../assets/images/dnd-banner.jpg';
import BannerYgo from '../assets/images/yugioh-banner.jpg';
import BannerMtG from '../assets/images/magic-banner.png';
import BannerOnePiece from '../assets/images/onepiece-banner.webp';
import BannerPokemon from '../assets/images/pokemon-banner.avif';

const Gallery = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying] = useState(true);
  const [isSingleSlide, setIsSingleSlide] = useState(false);
  const autoPlayRef = useRef(null);
  const touchStartXRef = useRef(null);

  // Memoize the initial filters to prevent unnecessary re-renders
  const initialFilters = useMemo(() => ({
    category: 'gallery',
    perPage: 10,
    orderBy: 'date',
    order: 'desc'
  }), []);

  // Fetch gallery posts
  const { posts, loading, error, fetchPosts } = usePosts({
    initialFilters,
    autoFetch: true
  });

  // Transform posts to slides format
  const slides = useMemo(() => {
    // Fallback slides in case no posts are available
    const fallbackSlides = [
      {
        id: 1,
        title: "DUNGEONS & DRAGONS",
        image: BannerDnd,
        alt: "Dungeons & Dragons - fantasy tabletop roleplaying game"
      },
      {
        id: 2,
        title: "YU-GI-OH!",
        image: BannerYgo,
        alt: "Yu-Gi-Oh! - trading card game and anime franchise"
      },
      {
        id: 3,
        title: "MAGIC: THE GATHERING",
        image: BannerMtG,
        alt: "Magic: The Gathering - fantasy trading card game"
      },
      {
        id: 4,
        title: "ONE PIECE",
        image: BannerOnePiece,
        alt: "One Piece - anime and manga franchise"
      },
      {
        id: 5,
        title: "POKÉMON",
        image: BannerPokemon,
        alt: "Pokémon - trading card game and media franchise"
      }
    ];

    return posts.length > 0 ? posts.map((post, index) => ({
      id: post.id,
      title: post.title.toUpperCase(),
      image: post.featuredImage || fallbackSlides[index % fallbackSlides.length]?.image,
      alt: post.title
    })) : fallbackSlides;
  }, [posts]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Touch swipe handlers (mobile)
  const handleTouchStart = (e) => {
    if (e.touches && e.touches.length > 0) {
      touchStartXRef.current = e.touches[0].clientX;
    }
  };

  const handleTouchEnd = (e) => {
    const startX = touchStartXRef.current;
    touchStartXRef.current = null;
    if (startX == null) return;
    const endX = (e.changedTouches && e.changedTouches.length > 0) ? e.changedTouches[0].clientX : startX;
    const deltaX = endX - startX;
    const threshold = 40; // px
    if (Math.abs(deltaX) > threshold) {
      if (deltaX < 0) {
        nextSlide();
      } else {
        prevSlide();
      }
    }
  };

  const handleShopNow = () => {
    const currentSlideData = slides[currentSlide];
    let brandFilter = '';
    
    // Map slide titles to brand filter values
    if (currentSlideData) {
      const title = currentSlideData.title.toUpperCase();
      
      if (title.includes('POKÉMON') || title.includes('POKEMON')) {
        brandFilter = 'Pokemon';
      } else if (title.includes('ONE PIECE')) {
        brandFilter = 'One Piece';
      } else if (title.includes('YU-GI-OH') || title.includes('YUGIOH')) {
        brandFilter = 'Yu-Gi-Oh';
      } else if (title.includes('MAGIC: THE GATHERING') || title.includes('MAGIC THE GATHERING')) {
        brandFilter = 'Magic: The Gathering';
      } else if (title.includes('DUNGEONS & DRAGONS') || title.includes('D&D')) {
        brandFilter = 'Dungeons & Dragons';
      }
    }
    
    // Navigate to shop with brand filter if available
    if (brandFilter) {
      navigate(`/shop?brand=${encodeURIComponent(brandFilter)}`);
    } else {
      navigate('/shop');
    }
  };

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 820px)');
    const apply = () => setIsSingleSlide(mq.matches);
    apply();
    mq.addEventListener('change', apply);

    if (isAutoPlaying) {
      autoPlayRef.current = setInterval(() => {
        nextSlide();
      }, 5000); // Change slide every 5 seconds
    }

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
      mq.removeEventListener('change', apply);
    };
  }, [isAutoPlaying, currentSlide, nextSlide]);

  const prevIndex = (currentSlide - 1 + slides.length) % slides.length;
  const nextIndex = (currentSlide + 1) % slides.length;

  // Show loading state
  if (loading) {
    return (
      <section className="gallery-carousel">
        <div className="carousel-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Loading size="lg" text="Loading gallery..." />
        </div>
      </section>
    );
  }

  // Show error state
  if (error) {
    return (
      <section className="gallery-carousel">
        <div className="carousel-container" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '400px', gap: '20px' }}>
          <p style={{ color: '#ff6b6b', fontSize: '18px' }}>Error loading gallery: {error}</p>
          <button 
            onClick={() => fetchPosts({ category: 'gallery', perPage: 10, orderBy: 'date', order: 'desc' })}
            className="btn btn-primary"
            style={{
              backgroundColor: '#14fefb',
              color: '#1a1a1a',
              border: 'none',
              padding: '13px 34px',
              fontSize: '16px',
              fontWeight: '500',
              textTransform: 'uppercase',
              fontFamily: 'Oswald, sans-serif',
              clipPath: 'polygon(90% 0, 100% 34%, 100% 100%, 10% 100%, 0 66%, 0 0)',
              transition: '0.15s ease-in-out'
            }}
          >
            Retry
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="gallery-carousel">
      <div className="carousel-container" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        {/* Navigation Arrows - hidden on small screens */}
        {!isSingleSlide && (
          <>
            <button 
              className="carousel-arrow carousel-arrow-left" 
              onClick={prevSlide}
              aria-label="Previous slide"
            >
              <ion-icon name="chevron-back-outline"></ion-icon>
            </button>
            <button 
              className="carousel-arrow carousel-arrow-right" 
              onClick={nextSlide}
              aria-label="Next slide"
            >
              <ion-icon name="chevron-forward-outline"></ion-icon>
            </button>
          </>
        )}

        {/* Carousel Slides - show prev/active/next on desktop; only active on mobile */}
        <div className="carousel-slides">
          {!isSingleSlide && (
            <div
              key={`prev-${prevIndex}`}
              className="carousel-slide prev"
              style={{
                left: '5%',
                transform: 'none',
                width: '60%',
                opacity: 0.6,
                zIndex: 2
              }}
              aria-hidden="true"
            >
              <div className="slide-content">
                <img src={slides[prevIndex].image} alt={slides[prevIndex].alt} />
                <div className="slide-overlay">
                  <h3 className="slide-title">{slides[prevIndex].title}</h3>
                </div>
              </div>
            </div>
          )}

          <div
            key={`active-${currentSlide}`}
            className="carousel-slide active"
            style={{
              left: '50%',
              transform: 'translateX(-50%)',
              width: isSingleSlide ? '100%' : '75%',
              zIndex: 3
            }}
          >
            <div className="slide-content">
              <img src={slides[currentSlide].image} alt={slides[currentSlide].alt} />
              <div className="slide-overlay">
                <h3 className="slide-title">{slides[currentSlide].title}</h3>
                <button className="btn btn-primary slide-shop-btn" onClick={handleShopNow}>
                  <span>Shop now</span>
                  <ion-icon name="cart-outline"></ion-icon>
                </button>
              </div>
            </div>
          </div>

          {!isSingleSlide && (
            <div
              key={`next-${nextIndex}`}
              className="carousel-slide next"
              style={{
                left: '95%',
                transform: 'translateX(-100%)',
                width: '60%',
                opacity: 0.6,
                zIndex: 2
              }}
              aria-hidden="true"
            >
              <div className="slide-content">
                <img src={slides[nextIndex].image} alt={slides[nextIndex].alt} />
                <div className="slide-overlay">
                  <h3 className="slide-title">{slides[nextIndex].title}</h3>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Carousel Indicators */}
        <div className="carousel-indicators">
          {slides.map((_, index) => (
            <button
              key={index}
              className={`carousel-indicator ${index === currentSlide ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Autoplay toggle removed for mobile-focused UX */}
      </div>
    </section>
  );
};

export default Gallery;
